/**
 * ScentMarkersLayer - Displays scent reports as interactive markers on the map
 */

import { useEffect, useState, useCallback } from 'react';
import { Marker, Popup } from 'react-map-gl';
import { motion, AnimatePresence } from 'framer-motion';
import { useSocialStore } from '@/stores/useSocialStore';
import { cn } from '@/utils/cn';
import type { ScentReport } from '@/types/social';

interface ScentMarkersLayerProps {
  visible?: boolean;
  onReportClick?: (report: ScentReport) => void;
}

// Scent category colors
const SCENT_COLORS: Record<string, string> = {
  floral: '#f472b6', // pink
  pine: '#22c55e', // green
  ocean: '#38bdf8', // sky blue
  coffee: '#a16207', // amber
  rain: '#6366f1', // indigo
  grass: '#84cc16', // lime
  bread: '#f59e0b', // amber
  jasmine: '#e879f9', // fuchsia
  citrus: '#fb923c', // orange
  herbs: '#4ade80', // green
  smoke: '#64748b', // slate
  industrial: '#ef4444', // red
  default: '#00ffd0', // cyan (brand color)
};

function getScentColor(scentNotes: string | null): string {
  if (!scentNotes) return SCENT_COLORS.default;
  const lower = scentNotes.toLowerCase();
  for (const [key, color] of Object.entries(SCENT_COLORS)) {
    if (lower.includes(key)) return color;
  }
  return SCENT_COLORS.default;
}

function getTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export function ScentMarkersLayer({ visible = true, onReportClick }: ScentMarkersLayerProps) {
  const feed = useSocialStore((state) => state.feed);
  const fetchFeed = useSocialStore((state) => state.fetchFeed);

  const [selectedReport, setSelectedReport] = useState<ScentReport | null>(null);
  const [verifiedReports, setVerifiedReports] = useState<Set<string>>(new Set());

  // Fetch reports on mount
  useEffect(() => {
    fetchFeed({ type: 'home' });
  }, [fetchFeed]);

  // Filter reports with valid coordinates
  const reportsWithLocation = feed.filter(
    (report) => report.latitude && report.longitude
  );

  const handleMarkerClick = useCallback((report: ScentReport) => {
    setSelectedReport(report);
    onReportClick?.(report);
  }, [onReportClick]);

  const handleVerify = useCallback((reportId: string) => {
    setVerifiedReports((prev) => new Set([...prev, reportId]));
    // TODO: Send verification to backend
  }, []);

  if (!visible) return null;

  return (
    <>
      {/* Scent Report Markers */}
      {reportsWithLocation.map((report) => {
        const color = getScentColor(report.scent_notes);
        const isVerified = verifiedReports.has(report.id);

        return (
          <Marker
            key={report.id}
            latitude={report.latitude!}
            longitude={report.longitude!}
            anchor="bottom"
            onClick={(e) => {
              e.originalEvent.stopPropagation();
              handleMarkerClick(report);
            }}
          >
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', damping: 15 }}
              className="cursor-pointer"
            >
              {/* Pulse ring for recent reports (< 1 hour) */}
              {new Date().getTime() - new Date(report.created_at).getTime() < 3600000 && (
                <div
                  className="absolute inset-0 rounded-full animate-ping"
                  style={{ backgroundColor: `${color}40` }}
                />
              )}

              {/* Marker */}
              <div
                className={cn(
                  'relative w-10 h-10 rounded-full',
                  'flex items-center justify-center',
                  'border-2 shadow-lg',
                  'transition-transform hover:scale-110'
                )}
                style={{
                  backgroundColor: `${color}20`,
                  borderColor: color,
                  boxShadow: `0 0 20px ${color}40`,
                }}
              >
                <span className="text-lg">
                  {report.scent_notes?.includes('smoke') || report.scent_notes?.includes('industrial')
                    ? '⚠️'
                    : '☁️'}
                </span>

                {/* Verification count badge */}
                {(report.likes_count > 0 || isVerified) && (
                  <div
                    className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-emerald-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-[#050a0f]"
                  >
                    {report.likes_count + (isVerified ? 1 : 0)}
                  </div>
                )}
              </div>
            </motion.div>
          </Marker>
        );
      })}

      {/* Selected Report Popup */}
      <AnimatePresence>
        {selectedReport && selectedReport.latitude && selectedReport.longitude && (
          <Popup
            latitude={selectedReport.latitude}
            longitude={selectedReport.longitude}
            anchor="bottom"
            offset={25}
            closeOnClick={false}
            onClose={() => setSelectedReport(null)}
            className="scent-popup"
          >
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="p-4 min-w-[280px] bg-[#0a1520] rounded-xl border border-cyan-500/30"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${getScentColor(selectedReport.scent_notes)}20` }}
                  >
                    <span className="text-sm">☁️</span>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">{getTimeAgo(selectedReport.created_at)}</p>
                    <p className="text-[10px] text-slate-500 truncate max-w-[150px]">
                      {selectedReport.location_name || 'Unknown location'}
                    </p>
                  </div>
                </div>
                {selectedReport.aqi && (
                  <div className="px-2 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20">
                    <span className="text-[10px] font-medium text-emerald-400">
                      AQI {selectedReport.aqi}
                    </span>
                  </div>
                )}
              </div>

              {/* Scent description */}
              <p className="text-white font-medium mb-3 leading-relaxed">
                "{selectedReport.scent_notes || 'No description'}"
              </p>

              {/* Reporter */}
              {selectedReport.description && (
                <p className="text-xs text-slate-400 mb-3">
                  {selectedReport.description.replace('Reporter: ', 'by ')}
                </p>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between pt-3 border-t border-slate-700/50">
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <span>✓ {selectedReport.likes_count + (verifiedReports.has(selectedReport.id) ? 1 : 0)} verified</span>
                </div>

                <button
                  onClick={() => handleVerify(selectedReport.id)}
                  disabled={verifiedReports.has(selectedReport.id)}
                  className={cn(
                    'px-3 py-1.5 rounded-full text-xs font-medium transition-all',
                    verifiedReports.has(selectedReport.id)
                      ? 'bg-emerald-500/20 text-emerald-400 cursor-default'
                      : 'bg-cyan-500/10 text-cyan-400 hover:bg-cyan-500/20 border border-cyan-500/30'
                  )}
                >
                  {verifiedReports.has(selectedReport.id) ? '✓ Verified' : '☁️ I smell it too'}
                </button>
              </div>
            </motion.div>
          </Popup>
        )}
      </AnimatePresence>

      {/* Custom popup styles */}
      <style>{`
        .scent-popup .mapboxgl-popup-content {
          background: transparent !important;
          padding: 0 !important;
          box-shadow: none !important;
        }
        .scent-popup .mapboxgl-popup-tip {
          display: none !important;
        }
        .scent-popup .mapboxgl-popup-close-button {
          color: white;
          font-size: 20px;
          padding: 4px 8px;
        }
      `}</style>
    </>
  );
}

export default ScentMarkersLayer;
